import {
  Box,
  Button,
  Container,
  Fab,
  Grid,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import SyncIcon from '@material-ui/icons/Sync';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import { Skeleton } from '@material-ui/lab';
import 'firebase/firestore';
import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { actions } from '../../redux/actions/actionsHandler';
import { updateUi } from '../../redux/actions/uiActions';
import { copyTextToClipboard } from '../../util/helpers/other';
import { getAllFromDividedQuery } from '../../util/helpers/redux';
import { parseTimestampToDate } from '../../util/helpers/server';
import { hasApiKeys } from '../../util/helpers/user';

const useStyles = makeStyles(({ palette, spacing, container }) => ({
  container: { ...container(2), marginLeft: 0 },
  table: { minWidth: '1024px' },
  tableHeader: {},
  tableBody: {},
  skeleton: { height: spacing(4) },
}));

const dialogTypes = {
  RENEW_API_KEY: 0,
  REMOVE_API_KEY: 1,
};

export default function ManageApiKeys(props) {
  const classes = useStyles();
  const intl = useIntl();
  const { palette, text, spacing } = useTheme();
  const dispatch = useDispatch();

  const { hasOwnedApiKeys, apiKeysData } = useSelector(({ firebase: { profile, auth }, firestore }) => ({
    hasOwnedApiKeys: hasApiKeys(profile),
    uid: auth.uid,
    apiKeysData: hasApiKeys(profile) ? getAllFromDividedQuery(firestore, 'APIKeys') : {},
  }));

  const MsInADay = 1000 * 60 * 60 * 24;
  const MsInAMonth = MsInADay * 30;
  const MsInAYear = MsInADay * 365;

  const { isLoaded: keysLoaded, data: apiKeys } = apiKeysData;

  const headers = {
    status: { id: 'manApiKeys.status', size: 1 },
    creationDate: { id: 'manApiKeys.creationDate', size: 1 },
    description: { id: 'description', size: 2 },
    apiKey: { id: 'manApiKeys.shortenedApiKey', size: 2 },
    authDomains: { id: 'manApiKeys.authDomains', size: 4 },
    subColumn: {
      size: 2,
      isSubcolumn: true,
      columns: {
        safetyLevel: { id: 'manApiKeys.safetyLevel', size: 2, justifyContent: 'center' },
        actions: { id: 'manApiKeys.actions', size: 10, justifyContent: 'space-evenly' },
      },
    },
  };

  const handleCopy = (text) => (e) => {
    copyTextToClipboard(text, () => {
      dispatch(
        updateUi({
          snackbar: {
            message: intl.formatMessage({ id: 'manApiKeys.keyCopied' }),
            severity: 'success',
            duration: 3000,
            show: true,
          },
        })
      );
    });
  };

  const showConfirmationDialog = (eventType, apiKeyId, apiKeyData) => (e) => {
    if (e) {
      e.preventDefault();
    }

    let msgId, confirmAction, actionData;
    switch (eventType) {
      case dialogTypes.RENEW_API_KEY:
        msgId = 'renewApiKey';
        confirmAction = actions.RENEW_API_KEY;
        actionData = { apiKeyId, description: apiKeyData.description };
        break;
      case dialogTypes.REMOVE_API_KEY: {
        msgId = 'removeApiKey';
        confirmAction = actions.REMOVE_API_KEY;
        actionData = { apiKeyId };
        break;
      }
      default:
        return;
    }

    dispatch(
      updateUi({
        dialog: {
          title: intl.formatMessage({ id: `manApiKeys.${msgId}Title` }),
          message: intl.formatMessage(
            { id: `manApiKeys.${msgId}Msg` },
            { description: apiKeyData.description, nl: <br key="nl0" /> }
          ),
          confirmAction,
          actionData,
          useCheckbox: true,
          checkboxState: false,
          checkboxMessage: <b>{intl.formatMessage({ id: `manApiKeys.${msgId}Check` })}</b>,
          show: true,
        },
      })
    );
  };

  const createRowData = (apiKeyId, apiKeyData) => {
    if (!apiKeyData) return null;

    let index = 0;
    const keyBase = apiKeyId.substring(4);
    const getKey = () => `${keyBase}${index++}`;

    const isInvalid = apiKeyData.status === 'INVALID';
    const style = isInvalid ? { color: palette.grey[600] } : {};
    const typogWrap = (content) => (
      <Typography style={style} key={getKey()}>
        {content}
      </Typography>
    );

    return {
      status: typogWrap(intl.formatMessage({ id: `manApiKeys.${apiKeyData.status}` })),
      creationDate: typogWrap(parseTimestampToDate(apiKeyData.creationDate).toLocaleDateString()),
      description: typogWrap(apiKeyData.description),
      authDomains: (
        <span
          style={{ display: 'flex', flexDirection: 'row', flexWrap: 'no-wrap', alignItems: 'center' }}
          key={getKey()}
        >
          <Typography style={{ wordWrap: 'anywhere', marginRight: 'auto', ...style }}>
            {apiKeyData.authDomains.length ? (
              apiKeyData.authDomains.map((domain, i) => (
                <Fragment key={`${domain}${i}`}>
                  {domain}
                  <br />
                </Fragment>
              ))
            ) : (
              <Box color={palette.grey[600]}>&lt;{intl.formatMessage({ id: 'none' })}&gt;</Box>
            )}
          </Typography>
          <Tooltip title={intl.formatMessage({ id: 'edit' })}>
            <span>
              <IconButton
                edge="end"
                aria-label={intl.formatMessage({ id: 'edit' })}
                component={Link}
                to={`/managekeys/${apiKeyId}/authDomains`}
                style={{ color: palette.info.main, margin: spacing(0, 1), ...style }}
                disabled={isInvalid}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        </span>
      ),
      apiKey: (
        <span
          style={{ display: 'flex', flexDirection: 'row', flexWrap: 'no-wrap', alignItems: 'center' }}
          key={getKey()}
        >
          <Typography style={{ wordWrap: 'anywhere', ...style }}>
            ...{apiKeyData.token.substr(apiKeyData.token.length - 60)}
          </Typography>
          <Tooltip title={intl.formatMessage({ id: 'copy' })}>
            <span>
              <IconButton
                edge="end"
                aria-label={intl.formatMessage({ id: 'copy' })}
                onClick={handleCopy(apiKeyData.token)}
                style={{ color: palette.info.main, margin: spacing(0, 1), ...style }}
                disabled={isInvalid}
              >
                <FileCopyOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </span>
      ),
      subColumn: {
        isSubcolumn: true,
        columns: {
          safetyLevel: (() => {
            const creationDate = parseTimestampToDate(apiKeyData.creationDate);
            const timeSinceCreationDate = new Date().getTime() - creationDate.getTime();

            if (isInvalid || timeSinceCreationDate < 9 * MsInAMonth) {
              return <CheckCircleOutlineIcon style={{ color: palette.success.main }} key={getKey()} />;
            } else if (timeSinceCreationDate < MsInAYear) {
              return <WarningRoundedIcon style={{ color: palette.warning.main }} key={getKey()} />;
            } else {
              return <WarningRoundedIcon style={{ color: palette.error.main }} key={getKey()} />;
            }
          })(),
          actions: (
            <Fragment key={getKey()}>
              <Tooltip title={intl.formatMessage({ id: 'renew' })}>
                <span>
                  <IconButton
                    edge="end"
                    aria-label={intl.formatMessage({ id: 'renew' })}
                    style={{ color: palette.info.main, ...style }}
                    onClick={showConfirmationDialog(dialogTypes.RENEW_API_KEY, apiKeyId, apiKeyData)}
                    disabled={isInvalid}
                  >
                    <SyncIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={intl.formatMessage({ id: 'remove' })}>
                <IconButton
                  edge="end"
                  aria-label={intl.formatMessage({ id: 'remove' })}
                  style={{ color: palette.error.main }}
                  onClick={showConfirmationDialog(dialogTypes.REMOVE_API_KEY, apiKeyId, apiKeyData)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Fragment>
          ),
        },
      },
    };
  };

  const justifyContentWrapper = (justifyContent, children) => (
    <div style={justifyContent ? { display: 'flex', justifyContent } : {}}> {children} </div>
  );

  return (
    <Container className={classes.container}>
      <Typography variant="h4" style={text.title}>
        {intl.formatMessage({ id: 'manApiKeys.title' })}
      </Typography>
      <Typography variant="subtitle1" style={text.subtitle}>
        {intl.formatMessage({ id: 'manApiKeys.subtitle' })}
      </Typography>

      <div>
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="center"
          alignItems="center"
          className={classes.table}
        >
          {/* Header */}
          {Object.values(headers).map((headerData, i) =>
            headerData.isSubcolumn ? (
              <Grid
                container
                item
                direction="row"
                justifyContent="center"
                alignItems="center"
                xs={headerData.size}
                key={`hc${i}`}
              >
                {Object.values(headerData.columns).map((subHeaderData, si) => (
                  <Grid item key={`hi${i}${si}`} xs={subHeaderData.size}>
                    {justifyContentWrapper(
                      subHeaderData.justifyContent,
                      <Typography>{intl.formatMessage({ id: subHeaderData.id })}</Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid item key={`hi${i}`} xs={headerData.size}>
                {justifyContentWrapper(
                  headerData.justifyContent,
                  <Typography> {intl.formatMessage({ id: headerData.id })}</Typography>
                )}
              </Grid>
            )
          )}
        </Grid>

        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="center"
          alignItems="center"
          className={classes.table}
        >
          {hasOwnedApiKeys ? (
            keysLoaded ? (
              Object.entries(apiKeys).map(([apiKeyId, apiKeyData], index) => {
                const rowData = createRowData(apiKeyId, apiKeyData);
                if (!rowData) return null;

                return Object.entries(headers).map(([headerKey, headerData], i) => {
                  const columnData = rowData[headerKey];

                  return headerData.isSubcolumn ? (
                    <Grid
                      container
                      item
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      xs={headerData.size}
                      key={`hvc${i}`}
                    >
                      {Object.entries(headerData.columns).map(([headerKey, subHeaderData], si) => {
                        const subColumnData = columnData.columns[headerKey];
                        return (
                          <Grid item key={`${headerKey}${i}${si}`} xs={subHeaderData.size}>
                            {justifyContentWrapper(subHeaderData.justifyContent, subColumnData)}
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Grid item xs={headerData.size} key={`${headerKey}${index}`}>
                      {justifyContentWrapper(headerData.justifyContent, columnData)}
                    </Grid>
                  );
                });
              })
            ) : (
              <Grid item xs={12}>
                <Skeleton type="rect" className={classes.skeleton} animation="wave" />
              </Grid>
            )
          ) : (
            <Grid item xs={12}>
              <Typography variant="h6">
                {intl.formatMessage(
                  { id: 'manApiKeys.noKeys' },
                  {
                    clickHere: (
                      <Button component={Link} to="/managekeys/create" size="small" key={'createKeyBtn'}>
                        {intl.formatMessage({ id: 'clickHere' })}
                      </Button>
                    ),
                  }
                )}
              </Typography>
            </Grid>
          )}
        </Grid>
      </div>

      <Fab
        variant="extended"
        size="medium"
        color="secondary"
        aria-label="add"
        component={Link}
        to="/managekeys/create"
        style={{ marginTop: spacing(4) }}
      >
        <AddCircleIcon style={{ marginRight: spacing(1) }} />
        {intl.formatMessage({ id: 'manApiKeys.createKey' })}
      </Fab>
    </Container>
  );
}
